import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectAllNotifications,
  selectVisibleNotifications,
  selectVisibleUnreadCount,
  selectConnectionStatus,
  hydrateNotifications,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
  notificationCategoryKey
} from '../store/slices/notificationsSlice';
import { NOTIFICATION_CATEGORIES } from '../config/notificationCategories';
import { notificationService } from '../services/api';
import { resolveNotificationPath } from '../utils/notificationPath';
import {
  canUseNotificationContextFilter,
  filterNotificationsByContext,
  contextTargetRole,
  contextForRole,
} from '../utils/notificationContext';
import { switchRole } from '../store/slices/authSlice';
import { HiOutlineBell } from 'react-icons/hi';
import { useUiLanguage } from '../components/profile/coaching-personality/useUiLanguage';
import i18n from '../i18n';
import { getStaticTranslation } from '../translations/registerStaticTranslations';
// WebSocket is now managed centrally by WebSocketManager

// Category filter + search (hub coverage extension, 2026-08). REGISTRY-COUPLED
// BY DESIGN: a notification row is fully described by what the backend
// registry (notify()) persists — text, category, redirect_path, image_url.
// The chips below derive from the CATEGORIES PRESENT IN THE LOADED ROWS via
// the shared classifier (notificationCategoryKey), labels come from the one
// category config (notificationCategories.js), and search matches the row
// text. A NEW backend event type therefore needs NO change on this page: its
// rows render, land in their category chip, and are searchable the moment the
// backend emits them. Only a brand-new CATEGORY needs a config entry — the
// same single registry line the settings screen already requires.
const CATEGORY_LABEL_BY_KEY = NOTIFICATION_CATEGORIES.reduce((acc, c) => {
  acc[c.key] = c.label;
  return acc;
}, {});
const CATEGORY_ORDER = NOTIFICATION_CATEGORIES.reduce((acc, c, i) => {
  acc[c.key] = i;
  return acc;
}, {});
const categoryLabelFor = (key) => CATEGORY_LABEL_BY_KEY[key] || key;

const Notifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // QAV5-502: window.confirm() is a native browser dialog the DOM scanner
  // cannot reach — the sentence is already hand-translated in
  // transfer-windows.json, so look it up before it goes to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  // Perspective-filtered (D-011): AgencyAdmin hides Messages + Meetings.
  const notifications = useSelector(selectVisibleNotifications);
  // Unfiltered store size — the only count comparable to the server's total,
  // which is likewise unfiltered by the perspective/read filters.
  const loadedCount = useSelector(selectAllNotifications).length;
  const unreadCount = useSelector(selectVisibleUnreadCount);
  const connectionStatus = useSelector(selectConnectionStatus);
  
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | category key
  const [searchQuery, setSearchQuery] = useState('');
  const authRoles = useSelector((state) => state.auth && state.auth.roles);
  const currentRole = useSelector((state) => state.auth && state.auth.currentRole);
  const showContextFilter = canUseNotificationContextFilter(authRoles);
  // The list belongs to the profile you are standing in — nothing to pick, and
  // therefore no state to keep in sync (Marin, 2026-07-31). Derived, not stored:
  // switching profile re-renders with the new scope by itself.
  const contextFilter = contextForRole(currentRole);
  const [isLoading, setIsLoading] = useState(false);

  // Server pagination: the list endpoint caps at 20 rows/page; rows 21+ are
  // reachable only by consuming pagination.next. The page fetches page 1 on
  // mount (learning whether more pages exist) and "Load more" merges the next
  // page into the store (hydrate dedups by id). The bell keeps showing only
  // the newest rows — this surface is the full history.
  // `total` is the server-side row count (pagination.count). It is what makes
  // "Load more" honest: `next` alone only says "page N+1 exists", not "there
  // are rows you cannot see yet" — and because hydrate is a UNION merge and
  // the store is pre-filled from the localStorage cache + live WebSocket
  // arrivals, the full history is regularly already rendered while page 1's
  // `next` is still non-null (T050-2: the button rendered, clicking it merged
  // rows that were already there, nothing changed).
  const [pageState, setPageState] = useState({ page: 1, hasMore: false, total: null });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = useCallback(async (page) => {
    const res = await notificationService.getAllNotifications({ page });
    if (res?.success && Array.isArray(res.data)) {
      dispatch(
        hydrateNotifications({
          list: res.data,
          unreadCount: res.pagination?.unread_count,
        })
      );
      setPageState({
        page,
        hasMore: !!res.pagination?.next,
        total:
          typeof res.pagination?.count === "number"
            ? res.pagination.count
            : null,
      });
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      await fetchPage(pageState.page + 1);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // WebSocket connection is now managed centrally by WebSocketManager

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    // T050-3: bare toLocaleDateString() follows the HOST machine's locale, so
    // rows older than 30 days rendered "5.6.2026" inside the English UI on a
    // German OS. Pin the locale like every other absolute date in the app —
    // same shape as src/utils/scoutFormat.js:18, which is the identical
    // "relative, then absolute for old rows" formatter. Not part of any
    // locale system: the i18n sweep is a runtime text translator and wires
    // nothing into date formatting.
    return time.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Mark a single notification read WITHOUT navigating. This is what the
  // "Mark Read" row action does: it must not deep-link (row click / "View" are
  // the navigating affordances).
  const handleMarkRead = async (notificationId) => {
    if (!notificationId) return;
    dispatch(markAsRead(notificationId));
    try {
      await notificationService.markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleNotificationClick = async (notificationId) => {
    if (!notificationId) return;

    const notificationObj = notifications.find((n) => n.id === notificationId);

    await handleMarkRead(notificationId);

    // Deep-link handling (shared resolver): server redirect_path first, then the
    // agent-tender + legacy meeting fallbacks. When nothing resolves we stay on
    // the page (this IS the notifications list).
    if (notificationObj) {
      const path = resolveNotificationPath(notificationObj);
      if (path) {
        // Cross-context click → switch the profile BEFORE navigating (W1-02),
        // same auth/switchRole mechanism as the manual profile switcher.
        const targetRole = contextTargetRole(
          notificationObj,
          authRoles,
          currentRole
        );
        if (targetRole) {
          dispatch(switchRole(targetRole));
        }
        navigate(path);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    setIsLoading(true);
    try {
      dispatch(markAllAsRead());
      await notificationService.markAllNotificationsAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      dispatch(removeNotification(notificationId));
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm(tr('Are you sure you want to clear all notifications? This action cannot be undone.'))) {
      setIsLoading(true);
      try {
        dispatch(clearNotifications());
        await notificationService.clearAllNotifications();
      } catch (error) {
        console.error('Failed to clear all notifications:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Context view first (dual-profile users), then the read-state filter.
  const contextNotifications = showContextFilter
    ? filterNotificationsByContext(notifications, contextFilter)
    : notifications;
  // "Load more" is only honest when rows exist that are NOT rendered yet:
  // the server says another page exists AND the store still holds fewer rows
  // than the server's total. Without the second half the control rendered for
  // an already-complete list and clicking it was a no-op (T050-2).
  const hasUnrenderedRows =
    pageState.hasMore &&
    (pageState.total === null || loadedCount < pageState.total);

  // Two counts live on this page and they are NOT the same quantity:
  //   * `unreadCount` is the SERVER-WIDE unread total (selectVisibleUnreadCount
  //     prefers pagination.unread_count so the badge is not capped at the 20-row
  //     page), and
  //   * `contextNotifications` is what is actually LOADED and in view.
  // Mixing them is what produced "39 unread • 20 total" in the header and a
  // literally negative "Read (-19)" tab. Each surface now uses one scale:
  //   — the header pairs the server-wide unread with the server-wide total
  //     (pagination.count; never below what is already loaded, since websocket
  //     arrivals and the localStorage cache can outrun the fetched page), and
  //   — the tab counts describe the list each tab produces, i.e. the loaded
  //     context rows, so All = Unread + Read holds by construction and no tab
  //     can promise rows the list cannot show.
  const totalCount =
    typeof pageState.total === 'number'
      ? Math.max(pageState.total, loadedCount)
      : loadedCount;
  const contextUnreadCount = contextNotifications.filter((n) => !n.read).length;
  const contextReadCount = contextNotifications.length - contextUnreadCount;

  // Read-state filter first (its tab counts describe the context list).
  const readFilteredNotifications = contextNotifications.filter(notification => {
    if (filter === 'unread') return !notification.read;
    if (filter === 'read') return notification.read;
    return true; // 'all'
  });

  // Category chips derive from the rows actually in view (read-state applied),
  // ordered like the settings screen, unknown categories appended last. A chip
  // only exists when at least one row carries its category, so a chip can
  // never render an a-priori empty list.
  const categoriesPresent = [
    ...new Set(
      readFilteredNotifications.map(notificationCategoryKey).filter(Boolean)
    ),
  ].sort((a, b) => {
    const ia = CATEGORY_ORDER[a] ?? NOTIFICATION_CATEGORIES.length;
    const ib = CATEGORY_ORDER[b] ?? NOTIFICATION_CATEGORIES.length;
    return ia === ib ? a.localeCompare(b) : ia - ib;
  });

  // If the active chip's last row disappeared (read/deleted/other tab), the
  // stale selection would silently show an empty list — fall back to All.
  const activeCategory =
    categoryFilter !== 'all' && !categoriesPresent.includes(categoryFilter)
      ? 'all'
      : categoryFilter;

  const categoryNotifications =
    activeCategory === 'all'
      ? readFilteredNotifications
      : readFilteredNotifications.filter(
          (n) => notificationCategoryKey(n) === activeCategory
        );

  // Search matches the row text and the category label, case-insensitive.
  // Every notification has text (the backend registry requires it), so every
  // type is findable.
  const query = searchQuery.trim().toLowerCase();
  const filteredNotifications = query
    ? categoryNotifications.filter((n) => {
        const text = String(n.text || '').toLowerCase();
        const label = categoryLabelFor(notificationCategoryKey(n)).toLowerCase();
        return text.includes(query) || label.includes(query);
      })
    : categoryNotifications;

  const hasNarrowingFilters = query !== '' || activeCategory !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 mt-1">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'} • {totalCount} total
              </p>
            </div>
            
            {/* Connection status indicator */}
            <div className="flex items-center gap-3">
              {connectionStatus === 'error' && (
                <span className="text-sm text-red-600 font-medium">
                  Connection Error
                </span>
              )}
              {connectionStatus === 'connecting' && (
                <span className="text-sm text-blue-600 font-medium">
                  Connecting...
                </span>
              )}
              {connectionStatus === 'connected' && (
                <span className="text-sm text-green-600 font-medium">
                  Connected
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center flex-wrap gap-3">
              {/* Filter Tabs */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All', count: contextNotifications.length },
                  { key: 'unread', label: 'Unread', count: contextUnreadCount },
                  { key: 'read', label: 'Read', count: contextReadCount }
                ].map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      filter === key
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {label} ({count})
                  </button>
                ))}
              </div>

              {/* The Agency / Personal tab row used to sit here. Removed on
                  Marin's call (2026-07-31): "obwohl ich nur haben wollte das
                  wenn ich im Agent Profil eingeloggt bin und auf Notification
                  gehe nur die Agent Notifications habe und umgekehrt. Mehr
                  nicht." Two filter rows for one question is the clutter he
                  reported; the profile switch in the header already answers it,
                  so the list is simply scoped and the row is gone. */}

              {/* Search — matches notification text and category label. */}
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notifications"
                aria-label="Search notifications"
                className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-56 max-w-full"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={isLoading}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                >
                  {isLoading ? 'Marking...' : 'Mark All Read'}
                </button>
              )}
              
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  disabled={isLoading}
                  className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Category chips — derived from the categories present in the
              loaded rows (registry-coupled: the backend category on each row
              decides, no per-type code here). Hidden while only one bucket
              exists: a single chip cannot narrow anything. */}
          {categoriesPresent.length > 1 && (
            <div className="flex items-center flex-wrap gap-2 mt-4">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                  activeCategory === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                All categories
              </button>
              {categoriesPresent.map((key) => {
                const count = readFilteredNotifications.filter(
                  (n) => notificationCategoryKey(n) === key
                ).length;
                return (
                  <button
                    key={key}
                    onClick={() => setCategoryFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      activeCategory === key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    {categoryLabelFor(key)} ({count})
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-2xl shadow-sm">
          {filteredNotifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(notification.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(notification.id);
                    }
                  }}
                  className={`p-6 hover:bg-gray-50 transition-colors border-l-4 cursor-pointer focus:outline-none focus:bg-gray-100 ${
                    notification.read ? 'border-transparent' : 'border-blue-500 bg-blue-50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Club logo (when present) with a clean icon fallback */}
                    {notification.image_url ? (
                      <img
                        src={notification.image_url}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5 border border-gray-200 bg-white"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex-shrink-0 mt-0.5 bg-gray-100 flex items-center justify-center">
                        <HiOutlineBell className="w-4 h-4 text-gray-400" />
                      </div>
                    )}

                    {/* Unread dot */}
                    <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                      notification.read ? 'bg-gray-300' : 'bg-blue-500'
                    }`} />

                    {/* Text and row actions share one line from sm up. On a
                        phone they stack: the row is only ~295px wide, and the
                        actions ("View" / "Mark Read" / "Delete") cannot shrink
                        below their words, so side by side they squeeze the text
                        down to a few characters per line. */}
                    <div className="flex-1 min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-relaxed ${
                          notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'
                        }`}>
                          {notification.text}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatNotificationTime(notification.created_at)}
                          {notificationCategoryKey(notification) && (
                            <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              {categoryLabelFor(
                                notificationCategoryKey(notification)
                              )}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-3">
                        {resolveNotificationPath(notification) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification.id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </button>
                        )}

                        {!notification.read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkRead(notification.id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Mark Read
                          </button>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(notification.id);
                          }}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {hasUnrenderedRows && (
                <div className="p-4 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                  >
                    {isLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {hasNarrowingFilters ? 'No notifications match your filters' :
                 showContextFilter && contextFilter !== 'all' ? 'No notifications for this filter' :
                 filter === 'unread' ? 'No unread notifications' :
                 filter === 'read' ? 'No read notifications' :
                 'No notifications yet'}
              </h3>
              <p className="text-gray-500">
                {hasNarrowingFilters ?
                  'Try a different search or category.' :
                 filter === 'all' ?
                  'You\'ll see your notifications here when they arrive.' :
                  `Switch to other tabs to see ${filter === 'unread' ? 'read' : 'unread'} notifications.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications; 